import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Spin } from 'antd';

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'header': [1, 2, 3, false] }],
    [{ 'align': [] }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    ['link'],
    ['clean']
  ],
};

const MAX_TITLE_LENGTH = 150;
const MAX_SUBTITLE_LENGTH = 1350;

const CreateHeroSlide = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [form, setForm] = useState({
    title: '',
    subtitle: ''
  });
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      axios.get(`${process.env.REACT_APP_API_URL}/api/hero/${id}`)
        .then(res => {
          const data = res.data.data;
          setForm({ 
            title: data.title, 
            subtitle: data.subtitle 
          });
          setPreview(`${process.env.REACT_APP_API_URL}/hero/${data.image_url}`);
        })
        .catch(() => toast.error("Failed to load slide"))
        .finally(() => setLoading(false));
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'title' && value.length > MAX_TITLE_LENGTH) return;
    setForm({ ...form, [name]: value });
  };

  const handleSubtitleChange = (value) => {
    if (value.length <= MAX_SUBTITLE_LENGTH) {
      setForm({ ...form, subtitle: value });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (form.title.length > MAX_TITLE_LENGTH) {
      toast.error(`Title must be ${MAX_TITLE_LENGTH} characters or less`);
      setIsSubmitting(false);
      return;
    }

    if (form.subtitle.length > MAX_SUBTITLE_LENGTH) {
      toast.error(`Subtitle must be ${MAX_SUBTITLE_LENGTH} characters or less`);
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append("title", form.title);
    formData.append("subtitle", form.subtitle);
    if (image) formData.append("image", image);

    try {
      if (id) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/hero/${id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toast.success("Slide updated successfully!");
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/hero`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toast.success("Slide created successfully!");
      }
      navigate('/admin/hero');
    } catch (err) {
      toast.error(err.response?.data?.message || "Operation failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto bg-white p-8 rounded-xl shadow-md mt-8">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-800">
          {id ? 'Edit Hero Slide' : 'Create New Hero Slide'}
        </h2>
        <p className="text-gray-600 mt-2">
          {id ? 'Update your hero slide content' : 'Add a new hero slide to your website'}
        </p>
      </div>
      
      <form onSubmit={submitForm} className="space-y-8">
        {/* Title and Image in one row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Title */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Title <span className="text-red-500">*</span>
              <span className="text-xs text-gray-500 ml-2">
                {form.title.length}/{MAX_TITLE_LENGTH} characters
              </span>
            </label>
            <input
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="Enter slide title"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
              maxLength={MAX_TITLE_LENGTH}
            />
            {form.title.length === MAX_TITLE_LENGTH && (
              <p className="text-xs text-red-500">Maximum characters reached</p>
            )}
          </div>

          {/* Image Upload */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Image {!id && <span className="text-red-500">*</span>}
            </label>
            <div className="flex items-center space-x-4">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">PNG, JPG, JPEG (Max 5MB)</p>
                </div>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageChange} 
                  className="hidden" 
                  {...(!id && { required: true })}
                />
              </label>
              {preview && (
                <div className="w-32 h-32 rounded-lg overflow-hidden border border-gray-200">
                  <img
                    src={preview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Subtitle */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Subtitle
            <span className="text-xs text-gray-500 ml-2">
              {form.subtitle.length}/{MAX_SUBTITLE_LENGTH} characters
            </span>
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            <ReactQuill
              value={form.subtitle}
              onChange={handleSubtitleChange}
              modules={quillModules}
              placeholder="Enter slide subtitle"
              className="h-64 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {form.subtitle.length >= MAX_SUBTITLE_LENGTH && (
            <p className="text-xs text-red-500">Maximum {MAX_SUBTITLE_LENGTH} characters reached</p>
          )}
        </div>

        {/* Buttons */}
        <div className="flex justify-end space-x-4 pt-4">
          <button
            type="button"
            onClick={() => navigate('/admin/hero')}
            className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-70"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <Spin size="small" className="mr-2" />
                {id ? 'Updating...' : 'Creating...'}
              </span>
            ) : (
              id ? 'Update Slide' : 'Create Slide'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateHeroSlide;